import {
  Box,
  Divider,
  HStack,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
  Text,
  Center,
  Spacer,
} from "@chakra-ui/react";
import React, { memo, PropsWithChildren, useMemo } from "react";

import colors from "app/colors";
import { FileGalleryFragment, ReactionType } from "gql/graphql";
import { sortReactionByType } from "modules/utils/Functions";
import {
  ReactionHeartColorIcon,
  ReactionLaughingColorIcon,
  ReactionSurpriseColorIcon,
  ReactionThumbsDownColorIcon,
  ReactionThumbsUpColorIcon,
} from "modules/icons";
import UserAvatar from "../../../_shared/user-avatar";
import UserMention from "../../../_shared/mentions/user-mention";
import UserName from "../../../_shared/user-name";
import ReactionsAvatarGroup from "../../_shared/reactions-avatar-group";

interface IUserReactions {
  userId: string;
  reactionTypes: ReactionType[];
}

interface IProps extends PropsWithChildren {
  file: FileGalleryFragment;
}

const MessageFileDrawerReactionsPopover: React.FC<IProps> = ({
  file,
  children,
}) => {
  const reactions = useMemo(() => {
    if (!file.reactionList) return [];
    return file.reactionList.filter((reaction) => reaction.fileId === file.id);
  }, [file.id, file.reactionList]);

  const reactionTypeCountList = useMemo(
    () =>
      reactions.sort(sortReactionByType).reduce(
        (acc, reaction) => {
          const reactionType = reaction.type as ReactionType;
          acc[reactionType] = (acc[reactionType] || 0) + 1;
          return acc;
        },
        {} as Record<ReactionType, number>,
      ),
    [reactions],
  );

  const userReactions = useMemo(() => {
    const userReactions: IUserReactions[] = [];
    reactions.forEach((reaction) => {
      const userReaction = userReactions.find(
        (userReaction) => userReaction.userId === reaction.userId,
      );
      if (userReaction && !userReaction.reactionTypes.includes(reaction.type)) {
        userReaction.reactionTypes.push(reaction.type);
      } else {
        userReactions.push({
          userId: reaction.userId,
          reactionTypes: [reaction.type],
        });
      }
    });
    return userReactions;
  }, [reactions]);

  return (
    <Popover trigger="hover" placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Box>{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="318px" boxShadow="0px 4px 14px 0px #0000001A;">
          <PopoverArrow />
          <VStack divider={<Divider />} spacing={2} w="full" px={2} py={2}>
            <VStack spacing={3}>
              <Text
                color={colors.blackSecondary}
                fontWeight={700}
                fontSize={16}
              >
                Reactions
              </Text>
              <Center>
                <HStack spacing={6} w="full" px={2}>
                  {Object.entries(reactionTypeCountList).map(
                    ([type, count]) => (
                      <HStack key={type} spacing={1}>
                        {type === ReactionType.Like && (
                          <ReactionThumbsUpColorIcon boxSize={5} />
                        )}
                        {type === ReactionType.Heart && (
                          <ReactionHeartColorIcon boxSize={5} />
                        )}
                        {type === ReactionType.Dislike && (
                          <ReactionThumbsDownColorIcon boxSize={5} />
                        )}
                        {type === ReactionType.Laughing && (
                          <ReactionLaughingColorIcon boxSize={5} />
                        )}
                        {type === ReactionType.Surprise && (
                          <ReactionSurpriseColorIcon boxSize={5} />
                        )}
                        <Text
                          color={colors.gray50}
                          fontWeight={500}
                          fontSize={14}
                        >
                          {count}
                        </Text>
                      </HStack>
                    ),
                  )}
                </HStack>
              </Center>
            </VStack>
            {userReactions.map((userReaction) => (
              <HStack key={userReaction.userId} spacing={2} w="full">
                <UserAvatar userId={userReaction.userId} size="sm" />
                <VStack spacing={0} alignItems="flex-start">
                  <UserMention
                    id={userReaction.userId}
                    fontWeight={700}
                    fontSize="14px"
                  />
                  <Text fontWeight={400} fontSize={14} color={colors.gray50}>
                    <UserName userId={userReaction.userId} />
                  </Text>
                </VStack>
                <Spacer />
                <HStack spacing={1}>
                  <ReactionsAvatarGroup
                    reactionTypes={userReaction.reactionTypes}
                  />
                </HStack>
              </HStack>
            ))}
          </VStack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(MessageFileDrawerReactionsPopover);
